/**
 * Iter 45r — Document Quick Session dialog.
 *
 * Unscheduled session documentation surface for providers who don't
 * want to launch the live data tracker. Mirrors the spec:
 *   - Pick student(s) from caseload
 *   - Pick date (defaults to today via localDateStr — never UTC)
 *   - Pick service minutes 10 / 15 / 30 (default 30)
 *   - Per-student attendance (Present / Absent / Unavailable)
 *   - Group summary
 *   - Optional per-student goal evidence (narrative, percent_trials,
 *     informal_assessment) — same rules as Log Session.
 *
 * FP3-035E (Feb 17, 2026) — Per-student attendance + multi-goal
 * evidence markup now lives in the shared
 * `SessionDocumentationRows` component. This dialog owns:
 *   - student picker
 *   - date / service-minutes selection
 *   - group summary
 *   - per-student + goal-row state
 *   - submit pipeline (createSession → logSession)
 *
 * Save flow:
 *   1) POST /sessions with delivered_minutes + is_quick=true; status
 *      stays "scheduled" until the log endpoint flips it.
 *   2) POST /sessions/{id}/log with attendance + evidence rows.
 *
 * Result: the session and any evidence land in Sessions tab, Data
 * tab (if chartable), and the goal progress chart with source label
 * "Quick Session" or "Logged Session" depending on the row.
 */
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Notepad } from "@phosphor-icons/react";
import { toast } from "sonner";
import { localDateStr } from "@/lib/utils";
import { DOCUMENT_SESSION_MINUTE_OPTIONS } from "@/lib/serviceDurations";
import api from "@/lib/api";
import SessionDocumentationRows, {
  makeInitialStudentRow,
  toggleGoalInRow,
  updateGoalRowField,
} from "@/components/log/SessionDocumentationRows";

const MINUTE_OPTIONS = DOCUMENT_SESSION_MINUTE_OPTIONS;

function addMinutesToHHMM(start, mins) {
  // start is "HH:MM" — guard for missing values
  const [h, m] = (start || "00:00").split(":").map((x) => parseInt(x, 10) || 0);
  const total = h * 60 + m + mins;
  const eh = Math.floor(total / 60) % 24;
  const em = total % 60;
  return `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
}

function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export default function DocumentQuickSessionDialog({
  open,
  onOpenChange,
  provider,
  onSaved,
  // FP3-114B — Provider unavailable is only a valid outcome for a
  // documentation flow tied to a real scheduled session / recurring
  // occurrence. This dialog is used by Quick Session → Document Session
  // for ad-hoc sessions from scratch (no scheduled reference), so the
  // outcome picker is HIDDEN by default. If a caller ever opens this
  // dialog with a genuine scheduled occurrence, they may pass
  // `scheduledSessionId` (or `scheduledRecurringBlockId`) to re-enable
  // the picker. Today, no caller does — the picker is effectively
  // absent in every DocumentQuickSession flow.
  scheduledSessionId = null,
  scheduledRecurringBlockId = null,
}) {
  const [caseload, setCaseload] = useState([]);
  const [selected, setSelected] = useState([]);
  const [date, setDate] = useState(localDateStr());
  const [minutes, setMinutes] = useState(30);
  const [groupSummary, setGroupSummary] = useState("");
  // FP3-114 — Shared group note opt-in disclosure.
  const [showGroupNote, setShowGroupNote] = useState(false);
  // FP3-114A — Session-level outcome (see LogSessionDialog).
  const [sessionOutcome, setSessionOutcome] = useState("held");
  const [providerUnavailableNote, setProviderUnavailableNote] = useState("");
  const [perStudent, setPerStudent] = useState({});
  const [goalsByStudent, setGoalsByStudent] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Reset whenever the dialog re-opens
  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setDate(localDateStr());
    setMinutes(30);
    setGroupSummary("");
    setShowGroupNote(false);
    setSessionOutcome("held");
    setProviderUnavailableNote("");
    setPerStudent({});
    setGoalsByStudent({});
  }, [open]);

  // Load caseload when the dialog opens
  useEffect(() => {
    if (!open || !provider?.id) return;
    api
      .getStudents({ provider_id: provider.id, status: "active" })
      .then((r) => setCaseload(r.data || []))
      .catch(() => setCaseload([]));
  }, [open, provider]);

  const selectedStudents = useMemo(
    () => caseload.filter((s) => selected.includes(s.id)),
    [caseload, selected],
  );

  // Whenever the selected student list changes, ensure perStudent has
  // a row for each AND fetch the active-goal list for each.
  //
  // FP3-034G — perStudent now carries:
  //   {
  //     attendance: "present" | "absent" | "unavailable",
  //     student_note: "shared narrative observation",
  //     goal_rows: [
  //       { goal_id, entry_type, numerator, denominator, assessment_label }
  //     ],
  //   }
  // goal_rows is an array so the provider can address multiple goals in
  // a single session. The shared `student_note` is attached as the
  // narrative_note on EACH selected goal's evidence row.
  useEffect(() => {
    if (!open) return;
    setPerStudent((prev) => {
      const next = { ...prev };
      for (const s of selectedStudents) {
        if (!next[s.id]) {
          next[s.id] = makeInitialStudentRow();
        }
      }
      // drop entries for de-selected students
      const allowed = new Set(selectedStudents.map((s) => s.id));
      for (const id of Object.keys(next)) {
        if (!allowed.has(id)) delete next[id];
      }
      return next;
    });
    // fetch goals
    const missing = selectedStudents.filter((s) => !goalsByStudent[s.id]);
    if (missing.length === 0) return;
    Promise.all(
      missing.map((s) =>
        api
          .getStudent(s.id)
          .then((r) => [s.id, (r.data?.goals || []).filter((g) => g.status === "active")])
          .catch(() => [s.id, []]),
      ),
    ).then((rows) => {
      setGoalsByStudent((g) => {
        const next = { ...g };
        rows.forEach(([sid, goals]) => {
          next[sid] = goals;
        });
        return next;
      });
    });
  }, [selectedStudents, open, goalsByStudent]);

  const toggleStudent = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  // ── State helpers — narrow callbacks the shared rows component
  // invokes. Defined here so each dialog can keep its own state
  // ownership while reusing the same UI.

  const setAttendance = (sid, value) => {
    setPerStudent((prev) => ({
      ...prev,
      [sid]: { ...(prev[sid] || makeInitialStudentRow()), attendance: value },
    }));
  };

  const setStudentNote = (sid, value) => {
    setPerStudent((prev) => ({
      ...prev,
      [sid]: { ...(prev[sid] || makeInitialStudentRow()), student_note: value },
    }));
  };

  const toggleGoal = (sid, goalId) => {
    setPerStudent((prev) => ({
      ...prev,
      [sid]: toggleGoalInRow(prev[sid], goalId),
    }));
  };

  const setGoalRowField = (sid, goalId, field, value) => {
    setPerStudent((prev) => {
      const cur = prev[sid];
      if (!cur) return prev;
      return { ...prev, [sid]: updateGoalRowField(cur, goalId, field, value) };
    });
  };

  const onMarkAllPresent = () => {
    setPerStudent((prev) => {
      const next = { ...prev };
      for (const s of selectedStudents) {
        next[s.id] = { ...(prev[s.id] || makeInitialStudentRow()), attendance: "present" };
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!provider?.id) {
      toast.error("No current provider — please retry");
      return;
    }
    if (selectedStudents.length === 0) {
      toast.error("Select at least one student");
      return;
    }
    setSubmitting(true);
    try {
      const startTime = nowHHMM();
      const endTime = addMinutesToHHMM(startTime, minutes);
      // 1) Create the unscheduled session row.
      const createRes = await api.createSession({
        student_ids: selectedStudents.map((s) => s.id),
        provider_id: provider.id,
        session_type: selectedStudents.length > 1 ? "group" : "individual",
        date,
        start_time: startTime,
        end_time: endTime,
        status: "scheduled",
        service_type: selectedStudents[0]?.services?.[0]?.label || "",
        is_recurring: false,
        delivered_minutes: minutes,
        tracker_elapsed_minutes: 0,
        is_quick: true,
      });
      const sessionId = createRes.data?.id;
      if (!sessionId) {
        toast.error("Could not create session — please try again");
        setSubmitting(false);
        return;
      }
      // FP3-114A — Provider-unavailable outcome short-circuits the
      // per-student build. Backend records outcome + note only.
      if (sessionOutcome === "provider_unavailable") {
        await api.logSession(sessionId, {
          session_id: sessionId,
          session_outcome: "provider_unavailable",
          provider_unavailable_note: providerUnavailableNote.trim(),
          students: [],
        });
        toast.success("Provider unavailability logged");
        onOpenChange(false);
        if (onSaved) onSaved();
        setSubmitting(false);
        return;
      }
      // 2) Build per-student log entries (attendance + optional evidence).
      //
      // FP3-034G — When a student has multiple selected goals we send
      // ONE entry per (student, goal) pair. The backend's log_session
      // already iterates `body.students` and writes one evidence row
      // per entry — duplicate `student_id` rows are fine (they share
      // attendance + session_note via the keyed merge below). The
      // shared `student_note` (narrative observation) is attached as
      // the narrative_note on every goal row so the goal-timeline
      // surfaces it under each addressed goal.
      const studentEntries = [];
      for (const s of selectedStudents) {
        const row = perStudent[s.id] || {};
        const attendance = row.attendance || "present";
        const sharedNote = (row.student_note || "").trim();
        const goalRows = attendance === "present" ? (row.goal_rows || []) : [];
        if (goalRows.length === 0) {
          // No goals selected (or not present) → single attendance-only entry.
          studentEntries.push({
            student_id: s.id,
            attendance,
            student_note: sharedNote,
          });
          continue;
        }
        // One entry per selected goal. Each carries the shared narrative
        // as student_note (the log endpoint stores it as narrative_note
        // on the evidence row) plus its own per-goal entry_type and
        // optional chartable numerator/denominator.
        for (const g of goalRows) {
          const entry = {
            student_id: s.id,
            attendance,
            student_note: sharedNote,
            goal_id: g.goal_id,
            entry_type: g.entry_type || "narrative_observation",
          };
          const n = parseInt(g.numerator, 10);
          const d = parseInt(g.denominator, 10);
          if (Number.isFinite(n)) entry.numerator = n;
          if (Number.isFinite(d)) entry.denominator = d;
          if (g.assessment_label) entry.assessment_label = g.assessment_label;
          studentEntries.push(entry);
        }
      }
      const logRes = await api.logSession(sessionId, {
        session_id: sessionId,
        session_outcome: "held",
        group_summary: groupSummary,
        students: studentEntries,
      });
      const evidenceCount = logRes.data?.evidence_written || 0;
      toast.success(
        evidenceCount > 0
          ? `Session documented · ${evidenceCount} progress ${evidenceCount === 1 ? "entry" : "entries"} saved`
          : "Session documented",
      );
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      console.error(err);
      toast.error("Could not document session — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // FP3-034I — Workspace-sized modal. Now that the dialog holds
        // multi-student / multi-goal evidence cards, a 2xl modal is
        // way too cramped at desktop widths. We open to ~90vw up to
        // 1200px on desktop, fall back to ~95vw on small screens,
        // and switch to a column flex layout so the body scrolls
        // independently of the (sticky) header + footer.
        className="w-[95vw] sm:w-[92vw] sm:max-w-[1200px] max-h-[90vh] p-0 bg-[#FBFAF6] flex flex-col gap-0 overflow-hidden"
        data-testid="document-quick-session-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#ECEAE4] shrink-0">
          <DialogTitle className="text-[#1F2937] heading-font flex items-center gap-2">
            <Notepad size={18} weight="duotone" className="text-[#9C6B2E]" />
            Document Session
          </DialogTitle>
          {/* FP3-035E follow-up — visually hidden description so
              Radix Dialog stops warning about missing aria
              description. The visible paragraph below is the
              human-facing helper text; this one is for assistive
              tech. */}
          <DialogDescription className="sr-only">
            Document an unscheduled session with attendance, service
            minutes, optional notes, and per-goal evidence.
          </DialogDescription>
          <p className="text-xs text-[#6B7280] mt-1">
            Log service, notes, goals addressed, and optional progress data without live data collection.
          </p>
        </DialogHeader>

        <div
          data-testid="doc-quick-body"
          className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3"
        >
          {/* Date + minutes */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-[#1F2937] mb-1 block">Date</Label>
              <Input
                data-testid="doc-quick-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-[#1F2937] mb-1 block">Service minutes</Label>
              {/* FP3-034H — 8 options (10–45 in 5-min steps) so providers
                  can document common district push-in / pull-out cycles
                  like 20 / 25 / 35 / 40 / 45 without fudging the count.
                  4-column grid keeps the chips legible at the modal width. */}
              <div className="grid grid-cols-4 gap-1" data-testid="doc-quick-minutes-selector">
                {MINUTE_OPTIONS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    data-testid={`doc-quick-minutes-${m}`}
                    onClick={() => setMinutes(m)}
                    className={`px-1 py-1.5 rounded-md text-xs font-medium border transition ${
                      minutes === m
                        ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                        : "bg-white text-[#6B7280] border-[#E5E3DB] hover:border-[#9B9890]"
                    }`}
                  >
                    {m} min
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-[#6B7280] mt-1">
                Applied to each present student. Absent / unavailable students get 0.
              </p>
            </div>
          </div>

          {/* Student picker */}
          <div>
            <Label className="text-xs font-medium text-[#1F2937] mb-1 block">
              Students <span className="text-[#6B7280] font-normal">({selected.length} selected)</span>
            </Label>
            <div className="border border-[#E5E3DB] rounded-xl bg-white">
              <ScrollArea className="h-48">
                <div className="p-2 space-y-0.5">
                  {caseload.length === 0 ? (
                    <p className="text-xs text-[#6B7280] p-3 text-center">Loading caseload…</p>
                  ) : (
                    caseload.map((s) => (
                      <label
                        key={s.id}
                        data-testid={`doc-quick-pick-${s.id}`}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F9F8F6] cursor-pointer"
                      >
                        <Checkbox
                          checked={selected.includes(s.id)}
                          onCheckedChange={() => toggleStudent(s.id)}
                        />
                        <div className="flex-1">
                          <span className="text-xs font-medium text-[#1F2937]">
                            {s.first_name} {s.last_name}
                          </span>
                          <span className="text-[10px] text-[#6B7280] ml-2">Gr. {s.grade}</span>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>

          {/* FP3-114A / FP3-114B — Session-level outcome picker. Only
              shown when the documentation flow is tied to a real
              scheduled session / recurring occurrence. Quick Session
              and Document-from-scratch flows have no scheduled
              reference, so the picker is hidden and the outcome is
              forcibly "held". */}
          {(scheduledSessionId || scheduledRecurringBlockId) && (
            <div data-testid="doc-quick-outcome-picker" className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wide text-[#6B7280] font-medium mr-1">
              Session outcome
            </span>
            {[
              { value: "held", label: "Session held" },
              { value: "provider_unavailable", label: "Provider unavailable" },
            ].map((opt) => {
              const active = sessionOutcome === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`doc-quick-outcome-${opt.value}`}
                  onClick={() => setSessionOutcome(opt.value)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition ${
                    active
                      ? opt.value === "provider_unavailable"
                        ? "bg-[#9C6B2E] text-white border-[#9C6B2E]"
                        : "bg-[#1E3F33] text-white border-[#1E3F33]"
                      : "bg-white text-[#6B7280] border-[#E5E3DB] hover:border-[#9B9890]"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          )}

          {sessionOutcome === "provider_unavailable" ? (
            <div
              data-testid="doc-quick-provider-unavailable-panel"
              className="rounded-xl border border-[#E7D6B4] bg-[#FBF6EC] p-4 space-y-2"
            >
              <p className="text-[12px] text-[#5A3E12] leading-snug">
                Session was not provided because the provider was unavailable.
                No student receives delivered minutes for this scheduled slot,
                and no student is marked absent or unavailable.
              </p>
              <div>
                <Label className="text-[11px] font-medium text-[#5A3E12] mb-1 block">
                  Provider unavailability note
                </Label>
                <Textarea
                  data-testid="doc-quick-provider-unavailable-note"
                  value={providerUnavailableNote}
                  onChange={(e) => setProviderUnavailableNote(e.target.value)}
                  placeholder="Briefly document why the session could not be provided."
                  className="min-h-[80px] text-sm bg-white"
                />
                <p className="text-[10px] text-[#6B7280] mt-1">
                  This note is stored on the session and shown to every participant's Session History.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* FP3-114 — Shared group note is now opt-in via a compact
                  affordance. Hidden entirely for single-student sessions
                  because the per-student note is sufficient. */}
              {selectedStudents.length > 1 && (
                <div>
                  {!showGroupNote && (
                    <button
                      type="button"
                      onClick={() => setShowGroupNote(true)}
                      data-testid="doc-quick-add-group-note"
                      className="text-xs font-medium text-[#1E3F33] hover:underline"
                    >
                      + Add shared group note
                    </button>
                  )}
                  {showGroupNote && (
                    <div data-testid="doc-quick-group-note-wrapper">
                      <Label className="text-xs font-medium text-[#1F2937] mb-1 block">
                        Shared group note
                      </Label>
                      <Textarea
                        data-testid="doc-quick-summary"
                        value={groupSummary}
                        onChange={(e) => setGroupSummary(e.target.value)}
                        placeholder="What did this session focus on? Any notable observations for the whole group?"
                        className="min-h-[70px] text-sm"
                      />
                      <p className="text-[10px] text-[#6B7280] mt-1">
                        This optional note applies to the overall session. Student-specific details belong in each student's note.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Per-student rows — extracted to SessionDocumentationRows
                  in FP3-035E so this dialog and LogSessionDialog share one
                  source of truth for the attendance + goal evidence
                  markup. */}
              <SessionDocumentationRows
                students={selectedStudents}
                perStudent={perStudent}
                goalsByStudent={goalsByStudent}
                testIdPrefix="doc-quick"
                setAttendance={setAttendance}
                setStudentNote={setStudentNote}
                toggleGoal={toggleGoal}
                setGoalRowField={setGoalRowField}
                onMarkAllPresent={onMarkAllPresent}
              />
            </>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t border-[#ECEAE4] bg-[#F6F4EE] shrink-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="doc-quick-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || selected.length === 0}
            data-testid="doc-quick-submit"
            className={
              sessionOutcome === "provider_unavailable"
                ? "bg-[#9C6B2E] hover:bg-[#7A5220] text-white"
                : "bg-[#1E3F33] hover:bg-[#173127] text-white"
            }
          >
            {submitting
              ? "Saving..."
              : sessionOutcome === "provider_unavailable"
                ? "Log provider unavailable"
                : "Document Session"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
